import { Injectable } from '@angular/core'
import { APPOINTMENT_REQUEST_TYPE, AppointmentRequest, AppointmentRequestTimesParams, AvailableFacilityTimeSlots, Facility, RestService } from '@mmx/shared'
import moment from 'moment-timezone'
import { BehaviorSubject, catchError, combineLatest, combineLatestWith, debounceTime, filter, map, Observable, switchMap, tap, throwError } from 'rxjs'

import { TimeSlotAvailableTimesData, TimeSlotPickerDateRange, TimeSlotPickerOptions } from '../components'

@Injectable()
export class TimeSlotPickerService {
  public loading$ = new BehaviorSubject(true)
  public timezone$ = new BehaviorSubject<string>('')

  public appointmentRequestType$ = new BehaviorSubject<APPOINTMENT_REQUEST_TYPE>(APPOINTMENT_REQUEST_TYPE.RESCHEDULE)
  public appointmentRequest$ = new BehaviorSubject<AppointmentRequest | null>(null)
  public facility$ = new BehaviorSubject<Facility | null>(null)

  public defaultAvailableTimes: TimeSlotAvailableTimesData | undefined
  public defaultTimeRange: TimeSlotPickerDateRange = {
    from: moment().local().startOf('day').format('YYYY-MM-DD'),
    to: moment().local().startOf('day').add(6, 'days').format('YYYY-MM-DD'),
  }
  public manualDateChange = false

  public dateRange$ = new BehaviorSubject<TimeSlotPickerDateRange>(this.defaultTimeRange)
  public schedulingQuestions$ = new BehaviorSubject<Record<string, any>>({})

  public timeSlotPickerOptions$: Observable<TimeSlotPickerOptions> = combineLatest([
    this.dateRange$,
    this.schedulingQuestions$,
  ]).pipe(
    combineLatestWith(
      this.appointmentRequest$.pipe(filter(Boolean)),
      this.appointmentRequestType$,
      this.facility$.pipe(filter(Boolean)),
      this.timezone$.pipe(filter(Boolean)),
    ),
    tap(() => this.loading$.next(true)),
    debounceTime(500),
    switchMap(([[dateRange, schedulingQuestions], appointmentRequest, appointmentRequestType, facility]) => {
      return this.getAppointmentRequestTimes({
        clinicId: appointmentRequest.clinicId!,
        facility: facility?.id ?? appointmentRequest.locationId!,
        appointmentRequestId: appointmentRequest.id,
        appointmentRequestType,
        start: moment(dateRange.from).format('YYYY-MM-DD'),
        end: moment(dateRange.to).format('YYYY-MM-DD'),
        schedulingQuestions: JSON.stringify(schedulingQuestions),
      }).pipe(
        map(timesData => ({ timesData, dateRange, facility })),
      )
    }),
    map(({ timesData, dateRange, facility }) => this.formatTimePickerOptions(timesData, dateRange, facility)),
    tap(() => this.loading$.next(false)),
    catchError((err) => {
      this.loading$.next(false)
      return throwError(() => err)
    }),
  )

  constructor(
    private restService: RestService,
  ) {}

  getAppointmentRequestTimes(input: AppointmentRequestTimesParams) {
    const route = '/appointment-requests/times'
    const params = this.restService.convertToParams(input as any)
    return this.restService.get<any>(route, params).pipe(
      map((resp) => resp.data[input.facility]),
    )
  }

  private formatTimePickerOptions(
    availableTimesData: AvailableFacilityTimeSlots,
    dateRange: TimeSlotPickerDateRange,
    facility: Facility,
  ): TimeSlotPickerOptions {
    const timezone = this.timezone$.getValue()
    const availableTimes = {
      timeSlots: availableTimesData.available.map(slot => moment.tz(slot, timezone)),
      recommended: availableTimesData.recommended.map(slot => moment.tz(slot, timezone)),
      soonest: availableTimesData.soonest?.map(slot => moment.tz(slot, timezone)),
    }

    if (dateRange.from === this.defaultTimeRange.from && dateRange.to === this.defaultTimeRange.to) {
      this.defaultAvailableTimes = {
        ...availableTimes,
      }
    }

    let manualDateChange = this.manualDateChange
    this.manualDateChange = false

    return {
      rules: availableTimesData.rules,
      dateRange,
      minNumberOfTimeSlots: availableTimesData.rules?.minNumberOfTimeSlots ?? 1,
      maxNumberOfTimeSlots: availableTimesData.rules?.maxNumberOfTimeSlots ?? 1,
      availableTimes,
      defaultAvailableTimes: this.defaultAvailableTimes!,
      shouldResetSelection: !manualDateChange,
      timezone: this.timezone$.getValue(),
      workingDays: facility.timing?.workingDays || {},
    }
  }
}
